import type { ThemeId } from '../../theme/ThemeContext'

/** 浅色毛玻璃主题：与右侧表单卡一致，减压模糊、抬高对比 */
export const LOGIN_CARD_SOFT_BLUR_THEMES = new Set<ThemeId>([
  'warm',
  'cool',
  'cartoon',
  'illustration',
])

export function loginShellRound(theme: ThemeId): string {
  const angular = theme === 'pixel' || theme === 'geek'
  return angular ? 'rounded-none' : 'rounded-[var(--radius-lg)]'
}

export function loginSurfaceFlags(theme: ThemeId) {
  const angular = theme === 'pixel' || theme === 'geek'
  const isGlass = theme === 'glass'
  const loginCardSoftBlur = LOGIN_CARD_SOFT_BLUR_THEMES.has(theme)
  return { angular, isGlass, loginCardSoftBlur }
}

/**
 * 毛玻璃可见度：底色须含透明分量（不能与 panel 100% 叠实），否则 backdrop-filter 几乎看不出。
 * 比例按主题在可读性与透景之间折中。
 */
const LOGIN_AUTH_SOFT_BLUR_BG =
  'backdrop-blur-[20px] bg-[color-mix(in_srgb,var(--panel)_62%,transparent)]'
const LOGIN_AUTH_DEFAULT_BG =
  'backdrop-blur-2xl bg-[color-mix(in_srgb,var(--panel)_58%,transparent)]'
const LOGIN_AUTH_GLASS_BG =
  'backdrop-blur-2xl bg-[color-mix(in_srgb,var(--panel)_46%,transparent)]'

/** 右侧账号登录卡：边框、毛玻璃、主题描边 / 卡通块影（与全局 token 一致） */
export function loginAuthCardSurfaceClass(theme: ThemeId): string {
  const { angular, isGlass, loginCardSoftBlur } = loginSurfaceFlags(theme)
  const surfaceBg = isGlass
    ? LOGIN_AUTH_GLASS_BG
    : loginCardSoftBlur
      ? LOGIN_AUTH_SOFT_BLUR_BG
      : LOGIN_AUTH_DEFAULT_BG
  return [
    'relative overflow-hidden border border-line shadow-float',
    surfaceBg,
    loginShellRound(theme),
    angular ? 'border-2' : '',
    isGlass ? 'ring-1 ring-white/14' : '',
    theme === 'cartoon' ? 'shadow-[6px_6px_0_var(--line)]' : '',
    'sm:shadow-[0_28px_64px_-12px_color-mix(in_srgb,var(--ink)_16%,transparent)]',
  ]
    .filter(Boolean)
    .join(' ')
}

/** 右侧卡最外层：布局 + 入场动画 + 落地投影（与表面类组合使用） */
export function loginAuthCardRootClass(theme: ThemeId): string {
  return [
    'mx-auto flex w-full max-w-[26rem] flex-col',
    'h-auto shrink-0',
    'lg:h-full lg:min-h-0 lg:self-center lg:overflow-hidden',
    'animate-fade-in-up sm:max-w-lg lg:mx-0 lg:max-w-[34rem]',
    'shadow-[0_19px_44px_-10px_color-mix(in_srgb,var(--ink)_18%,transparent)]',
    loginAuthCardSurfaceClass(theme),
  ].join(' ')
}

/** glass 主题顶部渐变罩层（与左侧 hero 电青气质对齐） */
export const loginAuthCardGlassOverlayClass =
  'pointer-events-none absolute inset-0 bg-gradient-to-br from-[#00bcd4]/[0.07] via-transparent to-transparent'

/** 右侧卡主体：移动端随内容增高；大屏不滚动，由外层 grid max-h 统一约束，padding 动态缩排 */
export const loginAuthCardBodyClass =
  'relative flex flex-none flex-col overflow-visible px-[calc(28*var(--rpx))] py-[calc(28*var(--rpx))] sm:px-8 sm:py-9 lg:min-h-0 lg:flex-1 lg:overflow-hidden lg:p-[clamp(0.875rem,1.8vh,1.75rem)] xl:p-[clamp(1rem,2vh,2.25rem)]'

/** 左侧介绍区：PC 与右侧登录卡共用 {@link loginAuthCardSurfaceClass}，视觉一致 */
export function loginHeroPanelClass(theme: ThemeId): string {
  const { angular } = loginSurfaceFlags(theme)

  return [
    'relative flex h-auto w-full flex-col animate-fade-in-up',
    'max-lg:min-h-0 max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none max-lg:backdrop-blur-none max-lg:ring-0 max-lg:px-0 max-lg:py-0',
    'mt-[calc(20*var(--rpx))] sm:mt-6 lg:mt-0',
    'lg:min-h-0 lg:self-center lg:overflow-hidden',
    loginAuthCardSurfaceClass(theme),
    angular
      ? 'max-lg:rounded-none lg:rounded-none lg:border-2 lg:shadow-[6px_6px_0_rgba(0,0,0,0.35)]'
      : 'max-lg:rounded-none lg:rounded-[var(--radius-lg)]',
    'lg:p-[clamp(1rem,2vh,2.5rem)] xl:p-[clamp(1.125rem,2.2vh,2.75rem)]',
  ].join(' ')
}

/** 表单内次要字色 / 分隔线，与毛玻璃透明度联动 */
export function loginAuthMutedClasses(theme: ThemeId): {
  muted: string
  mutedMid: string
  divider: string
} {
  const loginCardSoftBlur = LOGIN_CARD_SOFT_BLUR_THEMES.has(theme)
  return {
    muted: loginCardSoftBlur ? 'text-ink/85' : 'text-ink-soft',
    mutedMid: loginCardSoftBlur ? 'text-ink/92' : 'text-ink-soft',
    divider: loginCardSoftBlur ? 'bg-line/80' : 'bg-line/60',
  }
}

export function loginFieldRound(theme: ThemeId): string {
  return theme === 'pixel' || theme === 'geek' ? 'rounded-none' : 'rounded-[12px]'
}

export function loginPrimaryButtonRound(theme: ThemeId): string {
  return theme === 'pixel' || theme === 'geek'
    ? 'rounded-none border-2 border-line'
    : 'rounded-[12px]'
}
